import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  IntakeFieldDefinition,
  IntakeFieldResponse,
  DeviceSystemInfo,
} from '../services/types';
import {
  getIntakeSession,
  submitIntakeForm,
  requestRelink,
  getDeviceInfo,
} from '../services/intakeApi';

// Font detection constants (must match api/src/shared/types/intake.ts)
const FONT_BASE_WIDTH = 200;
const FONT_WIDTH_STEP = 3;
// Server floors new registrations at MIN_DEVICE_ID = 40 (encoded 320 units,
// clearly above the monospace fallback band). Frontend doesn't gate on MIN
// because existing pre-fix devices in the 1-39 range still detect fine via
// the measurement gate below — Gate 1 is what confirms font is loaded.
const MAX_DEVICE_ID = 20000;

type PageState = 'loading' | 'ready' | 'submitting' | 'submitted' | 'expired' | 'consumed' | 'invalid';

interface SessionData {
  ticketNumber: string;
  companyName: string;
  endUserName: string;
  formConfig: { fields: IntakeFieldDefinition[] };
  companyDevices: Array<{ deviceId: number; hostname: string }>;
}

interface DetectedDevice {
  deviceId: number;
  hostname: string;
  screenConnectGuid?: string;
  systemInfo?: DeviceSystemInfo;
}

export default function IntakeForm() {
  const { token } = useParams<{ token: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [session, setSession] = useState<SessionData | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string | string[] | boolean>>({});
  const [detectedDevice, setDetectedDevice] = useState<DetectedDevice | null>(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | undefined>();
  const [detectionMethod, setDetectionMethod] = useState<'font' | 'manual_selection' | undefined>();
  const [relinkSent, setRelinkSent] = useState(false);
  const [submittedTicket, setSubmittedTicket] = useState<{ ticketNumber: string; companyName: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Font-based device detection.
  //
  // TWO-GATE detection to avoid the "collision zone" ambiguity:
  //   1. Measurement gate \u2014 measure U+E000 in "UmetechDevice, monospace" vs
  //      "monospace" alone. If the widths differ by more than a few pixels, the
  //      UmetechDevice font IS loaded and rendered its own glyph. This is the
  //      only reliable signal that the font is really available.
  //   2. Range gate \u2014 decoded deviceId must be within [1, MAX_DEVICE_ID]. IDs
  //      below MIN_DEVICE_ID are still allowed here (existing pre-fix devices
  //      in the 17-39 range remain detectable) but never assigned to new
  //      devices going forward.
  const detectDevice = useCallback(async () => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Measure U+E000 rendered against the UmetechDevice font. The font's
      // glyph has advanceWidth = FONT_BASE_WIDTH + deviceId*FONT_WIDTH_STEP.
      ctx.font = '100px UmetechDevice, monospace';
      const deviceWidth = ctx.measureText('\uE000').width;

      // Baseline: same glyph rendered against monospace only, which will use
      // the browser's monospace fallback (typically `.notdef` at ~20px on
      // Windows Consolas, up to ~40px on some macOS monospaces).
      ctx.font = '100px monospace';
      const monoWidth = ctx.measureText('\uE000').width;

      // Gate 1: If the two measurements are essentially identical, the custom
      // font never resolved \u2014 browser fell back to monospace both times, so
      // there's no encoded signal. Threshold of 3px handles sub-pixel
      // measurement noise while catching all encoded widths from deviceId >= 15
      // (advanceWidth 245 units \u2192 24.5px = ~5px above typical fallback).
      if (Math.abs(deviceWidth - monoWidth) <= 3) return;

      // Font is rendered at 100px with unitsPerEm=1000, so scale factor = 0.1.
      // measuredWidthPx = advanceWidth * 0.1 \u2192 advanceWidth = measuredWidthPx * 10
      const advanceWidth = Math.round(deviceWidth * 10);
      const deviceId = Math.round((advanceWidth - FONT_BASE_WIDTH) / FONT_WIDTH_STEP);

      // Gate 2: Decoded ID must be in the valid range. MIN_DEVICE_ID (40) is
      // the assignment floor on the server, but existing pre-fix devices in the
      // 1-39 range detect fine here because Gate 1 already confirmed the font
      // is loaded. MAX_DEVICE_ID (20000) is the capacity ceiling.
      if (deviceId >= 1 && deviceId <= MAX_DEVICE_ID) {
        const result = await getDeviceInfo(deviceId);
        if (result.success && result.data) {
          setDetectedDevice(result.data);
          setSelectedDeviceId(deviceId);
          setDetectionMethod('font');
        }
      }
    } catch {
      // Font detection failed silently - user can select manually
    }
  }, []);

  // Intake pages: remove PWA prompts, fix scrolling (global styles lock overflow:hidden)
  useEffect(() => {
    // Remove manifest link so browser doesn't trigger install prompt
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) manifestLink.remove();
    // Unregister service worker for this page
    navigator.serviceWorker?.getRegistrations().then(regs => {
      for (const reg of regs) reg.unregister();
    });
    // Suppress beforeinstallprompt
    const handler = (e: Event) => e.preventDefault();
    window.addEventListener('beforeinstallprompt', handler);
    // Fix scrolling — global index.html sets overflow:hidden!important on html/body/#root
    // Must use setProperty with 'important' to override !important CSS
    document.documentElement.style.setProperty('overflow', 'auto', 'important');
    document.documentElement.style.setProperty('height', 'auto', 'important');
    document.body.style.setProperty('overflow', 'auto', 'important');
    document.body.style.setProperty('height', 'auto', 'important');
    document.body.style.setProperty('margin', '0');
    const root = document.getElementById('root');
    if (root) {
      root.style.setProperty('overflow', 'auto', 'important');
      root.style.setProperty('height', 'auto', 'important');
    }
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Load session
  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      return;
    }

    (async () => {
      const result = await getIntakeSession(token);

      if (result.success && result.data) {
        setSession(result.data);
        setPageState('ready');
        // Try device detection after a brief delay for font rendering
        setTimeout(() => detectDevice(), 500);
      } else {
        switch (result.error) {
          case 'expired':
            setPageState('expired');
            break;
          case 'consumed':
            setPageState('consumed');
            break;
          case 'invalidated':
          default:
            setPageState('invalid');
        }
      }
    })();
  }, [token, detectDevice]);

  const handleFieldChange = (fieldId: string, value: string | string[] | boolean) => {
    setFieldValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleManualDeviceSelect = async (deviceId: number) => {
    setSelectedDeviceId(deviceId);
    setDetectionMethod('manual_selection');
    try {
      const result = await getDeviceInfo(deviceId);
      if (result.success && result.data) {
        setDetectedDevice(result.data);
      }
    } catch {
      // Ignore - device info is optional
    }
  };

  const handleSubmit = async () => {
    if (!token || !session) return;
    setError(null);

    // Validate required fields
    const missingFields = session.formConfig.fields
      .filter(f => f.required && f.type !== 'device_select')
      .filter(f => {
        const val = fieldValues[f.id];
        return !val || (typeof val === 'string' && val.trim() === '');
      });

    if (missingFields.length > 0) {
      setError(`Please fill in: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    setPageState('submitting');

    const fields: IntakeFieldResponse[] = session.formConfig.fields
      .filter(f => f.type !== 'device_select')
      .map(f => ({
        fieldId: f.id,
        fieldLabel: f.label,
        fieldType: f.type,
        value: fieldValues[f.id] ?? '',
      }));

    const result = await submitIntakeForm(token, {
      fields,
      detectedDeviceId: selectedDeviceId,
      detectedHostname: detectedDevice?.hostname,
      screenConnectGuid: detectedDevice?.screenConnectGuid,
      systemInfo: detectedDevice?.systemInfo,
      deviceDetectionMethod: detectionMethod,
      userAgent: navigator.userAgent,
    });

    if (result.success && result.data) {
      setSubmittedTicket(result.data);
      setPageState('submitted');
    } else {
      setError(result.error || 'Failed to submit form');
      setPageState('ready');
    }
  };

  const handleRelink = async () => {
    if (!token) return;
    const result = await requestRelink(token);
    if (result.success) {
      setRelinkSent(true);
    }
  };

  // Generate system health warnings
  const getWarnings = (): string[] => {
    if (!detectedDevice?.systemInfo) return [];
    const info = detectedDevice.systemInfo;
    const warnings: string[] = [];

    if (info.uptimeDays !== undefined && info.uptimeDays > 3) {
      warnings.push(`Your computer hasn't been restarted in ${info.uptimeDays} days. A quick restart before your tech connects can often fix common issues!`);
    }
    if (info.pendingReboot) {
      warnings.push('A Windows Update restart is pending.');
    }
    if (info.diskDrives) {
      for (const disk of info.diskDrives) {
        if (disk.freePercent < 10) {
          warnings.push(`Your ${disk.drive} drive is running low on space (${disk.freePercent}% free).`);
        }
      }
    }
    return warnings;
  };

  // =========================================================================
  // Render States
  // =========================================================================

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (pageState === 'expired') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="text-5xl mb-4">&#9203;</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Link Expired</h1>
          <p className="text-gray-600">This intake form link has expired. Please contact your tech for a new link.</p>
        </div>
      </div>
    );
  }

  if (pageState === 'consumed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="text-5xl mb-4">&#9989;</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Already Submitted</h1>
          <p className="text-gray-600 mb-4">This form has already been filled out.</p>
          {!relinkSent ? (
            <button
              onClick={handleRelink}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Request New Link
            </button>
          ) : (
            <p className="text-green-600 font-medium">Your tech has been notified and will send a new link.</p>
          )}
        </div>
      </div>
    );
  }

  if (pageState === 'invalid') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="text-5xl mb-4">&#10060;</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid Link</h1>
          <p className="text-gray-600">This link is not valid. Please contact your tech for assistance.</p>
        </div>
      </div>
    );
  }

  if (pageState === 'submitted') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="text-5xl mb-4">&#127881;</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-2">Your information has been submitted successfully.</p>
          {submittedTicket && (
            <p className="text-sm text-gray-500">
              Ticket #{submittedTicket.ticketNumber} &mdash; {submittedTicket.companyName}
            </p>
          )}
          <p className="text-gray-600 mt-4">Your tech has been notified and will reach out to you shortly.</p>
        </div>
      </div>
    );
  }

  // =========================================================================
  // Main Form (ready / submitting)
  // =========================================================================

  const warnings = getWarnings();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#1e3a5f] text-white py-5 px-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-lg font-bold">Umetech Support</h1>
          {session && (
            <p className="text-sm text-blue-200 mt-1">
              Ticket #{session.ticketNumber} &mdash; {session.companyName}
            </p>
          )}
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-4">
        {/* Greeting */}
        {session && (
          <p className="text-gray-700 mb-4">
            Hi <span className="font-semibold">{session.endUserName}</span>, please fill out the form below so we can help you faster.
          </p>
        )}

        {/* Device Detection Card */}
        {detectedDevice && (
          <div className="bg-white rounded-xl shadow-sm border border-green-200 p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                &#128187; {detectedDevice.hostname}
              </span>
              {detectionMethod === 'font' && (
                <span className="text-xs text-gray-400">Auto-detected</span>
              )}
            </div>

            {detectedDevice.systemInfo && (
              <div className="text-sm text-gray-600 space-y-1">
                {detectedDevice.systemInfo.operatingSystem && (
                  <p><span className="font-medium">OS:</span> {detectedDevice.systemInfo.operatingSystem}</p>
                )}
                {detectedDevice.systemInfo.manufacturer && detectedDevice.systemInfo.model && (
                  <p><span className="font-medium">Hardware:</span> {detectedDevice.systemInfo.manufacturer} {detectedDevice.systemInfo.model}</p>
                )}
                {detectedDevice.systemInfo.totalRamGB && (
                  <p><span className="font-medium">Memory:</span> {detectedDevice.systemInfo.totalRamGB} GB RAM</p>
                )}
                {detectedDevice.systemInfo.diskDrives && detectedDevice.systemInfo.diskDrives.length > 0 && (
                  <div>
                    {detectedDevice.systemInfo.diskDrives.map(disk => (
                      <div key={disk.drive} className="flex items-center gap-2 mt-1">
                        <span className="font-medium text-xs w-8">{disk.drive}</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              disk.freePercent < 10 ? 'bg-red-500' :
                              disk.freePercent < 25 ? 'bg-amber-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${100 - disk.freePercent}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">{disk.freeGB.toFixed(0)} GB free</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Warnings */}
            {warnings.length > 0 && (
              <div className="mt-3 space-y-2">
                {warnings.map((w, i) => (
                  <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    &#9888;&#65039; {w}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Form Fields */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-4">
          {session?.formConfig.fields
            .sort((a, b) => a.order - b.order)
            .map(field => (
              <FormField
                key={field.id}
                field={field}
                value={fieldValues[field.id]}
                onChange={(val) => handleFieldChange(field.id, val)}
                companyDevices={session.companyDevices}
                detectedDevice={detectedDevice}
                selectedDeviceId={selectedDeviceId}
                onDeviceSelect={handleManualDeviceSelect}
              />
            ))}
        </div>

        {/* Submit */}
        <div className="mt-6 mb-8">
          <button
            onClick={handleSubmit}
            disabled={pageState === 'submitting'}
            className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {pageState === 'submitting' ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Form Field Component
// ============================================================================

function FormField({
  field,
  value,
  onChange,
  companyDevices,
  detectedDevice,
  selectedDeviceId,
  onDeviceSelect,
}: {
  field: IntakeFieldDefinition;
  value: string | string[] | boolean | undefined;
  onChange: (val: string | string[] | boolean) => void;
  companyDevices: Array<{ deviceId: number; hostname: string }>;
  detectedDevice: DetectedDevice | null;
  selectedDeviceId: number | undefined;
  onDeviceSelect: (deviceId: number) => void;
}) {
  const inputClasses = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-900';

  // Device select field
  if (field.type === 'device_select') {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {field.label}
          {field.helpText && <span className="block text-xs text-gray-400 font-normal">{field.helpText}</span>}
        </label>
        {detectedDevice ? (
          <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
            <span className="text-green-700 font-medium">{detectedDevice.hostname}</span>
            <span className="text-xs text-green-500">(detected)</span>
          </div>
        ) : companyDevices.length > 0 ? (
          <select
            className={inputClasses}
            value={selectedDeviceId ?? ''}
            onChange={e => {
              const id = parseInt(e.target.value, 10);
              if (!isNaN(id)) onDeviceSelect(id);
            }}
          >
            <option value="">Select your computer...</option>
            {companyDevices.map(d => (
              <option key={d.deviceId} value={d.deviceId}>{d.hostname}</option>
            ))}
          </select>
        ) : (
          <p className="text-sm text-gray-400 italic">No registered devices found</p>
        )}
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {field.label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
        {field.helpText && <span className="block text-xs text-gray-400 font-normal">{field.helpText}</span>}
      </label>

      {field.type === 'text' && (
        <input
          type="text"
          className={inputClasses}
          placeholder={field.placeholder}
          value={(value as string) || ''}
          onChange={e => onChange(e.target.value)}
        />
      )}

      {field.type === 'textarea' && (
        <textarea
          className={`${inputClasses} min-h-[100px]`}
          placeholder={field.placeholder}
          value={(value as string) || ''}
          onChange={e => onChange(e.target.value)}
        />
      )}

      {field.type === 'dropdown' && (
        <select
          className={inputClasses}
          value={(value as string) || ''}
          onChange={e => onChange(e.target.value)}
        >
          <option value="">Select...</option>
          {field.options?.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      )}

      {field.type === 'radio' && (
        <div className="space-y-2">
          {field.options?.map(opt => (
            <label key={opt} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={field.id}
                value={opt}
                checked={value === opt}
                onChange={() => onChange(opt)}
                className="text-blue-600"
              />
              <span className="text-gray-700">{opt}</span>
            </label>
          ))}
        </div>
      )}

      {field.type === 'checkbox' && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={value === true}
            onChange={e => onChange(e.target.checked)}
            className="text-blue-600 rounded"
          />
          <span className="text-gray-700">{field.placeholder || 'Yes'}</span>
        </label>
      )}
    </div>
  );
}
